import React, { useState, useEffect, useRef } from 'react';
import './Reading.css';
import SettingsModal from '../../components/SettingsModal';
import { readingConfig } from '../../config/settingsConfig';
import TRecorder from 'recorder-core';
import 'recorder-core/src/engine/mp3';
import 'recorder-core/src/engine/mp3-engine';
import { voiceAPI } from '../../services/api';

// 导入图片资源
import logoImage from '../../static/image/白字logo.png';
import listenIcon from '../../static/image/听.png';
import speakIcon from '../../static/image/说.png';
import scoreGoodImage from '../../static/image/测评好.png';
import scoreBadImage from '../../static/image/测评差.png';
import scoringImage from '../../static/image/测评中.png';

const Reading = () => {
  const [selectedWords, setSelectedWords] = useState([]);
  const selectedWordsRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 领读状态管理
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // 单词列表项的 ref 数组
  const wordListItemsRef = useRef([]);

  // 格式化单词标题，将每个字母包裹在 <span> 标签中
  const formatWordTitle = (title) => {
    if (!title) return '';
    let formattedTitle = '';
    for (let i = 0; i < title.length; i++) {
      const char = title.charAt(i);
      if (char === ' ') {
        // 空格使用特殊的 span 标签，加大间距
        formattedTitle += `<span class="word-space"></span>`;
      } else {
        // 所有字符（包括问号）都使用 letter 样式
        formattedTitle += `<span class="letter">${char}</span>`;
      }
    }
    return formattedTitle;
  };

  // 根据单词长度计算动态字体大小和行高
  const getWordStyle = (title) => {
    if (!title) return {};
    
    const length = title.length;
    
    // 根据屏幕宽度获取基准字体大小
    const screenWidth = window.innerWidth;
    let baseFontSize = 120;
    let baseLineHeight = 150;
    
    if (screenWidth <= 768) {
      baseFontSize = 80; // 保持较大字体，不缩小
      baseLineHeight = 100;
    } else if (screenWidth <= 1280) {
      baseFontSize = 100;
      baseLineHeight = 120;
    } else if (screenWidth <= 1600) {
      baseFontSize = 120;
      baseLineHeight = 150;
    }
    
    // 根据字符长度动态缩小字体（相对于基准值）
    // 单词越长，字体越小，确保不换行
    let fontSize = baseFontSize;
    let lineHeight = baseLineHeight;
    
    if (length > 30) {
      fontSize = baseFontSize * 0.35;  // 超长单词：缩小到35%
      lineHeight = baseLineHeight * 0.4;
    } else if (length > 25) {
      fontSize = baseFontSize * 0.4;  // 很长单词：缩小到40%
      lineHeight = baseLineHeight * 0.45;
    } else if (length > 20) {
      fontSize = baseFontSize * 0.5;  // 长单词：缩小到50%
      lineHeight = baseLineHeight * 0.53;
    } else if (length > 15) {
      fontSize = baseFontSize * 0.67;  // 较长单词：缩小到67%
      lineHeight = baseLineHeight * 0.67;
    } else if (length > 10) {
      fontSize = baseFontSize * 0.83;  // 中等单词：缩小到83%
      lineHeight = baseLineHeight * 0.8;
    }
    
    return {
      fontSize: `${fontSize}px`,
      lineHeight: `${lineHeight}px`,
      '--letter-font-size': `${fontSize}px`,
      '--letter-line-height': `${lineHeight}px`,
      '--space-height': `${lineHeight}px`
    };
  };
  
  // 自动播放状态
  const [isAutoPlaying, setIsAutoPlaying] = useState(false); // 默认暂停
  const isAutoPlayingRef = useRef(false);
  
  // 领读设置弹窗状态
  const [showReadingSettings, setShowReadingSettings] = useState(false);
  
  // 图片显示控制
  const [showImage, setShowImage] = useState(true); // 默认显示图片
  const [showPhonetic, setShowPhonetic] = useState(true); // 默认显示音标
  const [showTranslation, setShowTranslation] = useState(true); // 默认显示翻译
  const [imageLoadError, setImageLoadError] = useState(false); // 当前单词图片是否加载失败
  
  // 全局音频管理
  const [currentAudio, setCurrentAudio] = useState(null);
  
  // 使用ref来跟踪播放状态
  const isPlayingRef = useRef(false);
  const playbackIdRef = useRef(0);
  const navigationTimeoutRef = useRef(null);
  const currentWordIndexRef = useRef(0);
  
  // 倒计时状态
  const [countdown, setCountdown] = useState(0); // 0表示未开始倒计时
  const countdownTimerRef = useRef(null);
  
  // 录音相关状态
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingPowerLevel, setRecordingPowerLevel] = useState(0);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [showScoreImage, setShowScoreImage] = useState(false);
  const [scoreImageType, setScoreImageType] = useState('');
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null); // 保存录音的 URL
  const recordedAudioUrlRef = useRef(null); // 使用 ref 保存 URL，确保能正确清理
  const recorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  
  // 领读设置
  const [readingSettings, setReadingSettings] = useState({
    playCount: 2,
    pauseTime: 1,
    playSpeed: 'normal',
    repeatCount: 'playAfterAll'
  });
  const readingSettingsRef = useRef(readingSettings);

  // 创建音频引用
  const currentAudioRef = useRef(null);
  
  // 同步 currentAudio 到 ref
  useEffect(() => {
    currentAudioRef.current = currentAudio;
  }, [currentAudio]);
  
  // 初始化录音器
  const initRecorder = () => {
    if (recorderRef.current) {
      return; // 已经初始化过了
    }
    
    recorderRef.current = TRecorder({
      type: "mp3",           // 录音格式：MP3
      bitRate: 128,          // 比特率：128kbps
      sampleRate: 44100,     // 采样率：44100Hz
      onProcess: function (buffers, powerLevel, duration, sampleRate) {
        setRecordingDuration(Math.floor(duration / 1000)); // 转换为秒
        setRecordingPowerLevel(powerLevel);
      }
    });
  };
  
  // 设置页面特殊标识
  useEffect(() => {
    document.body.classList.add('reading-page');
    
    return () => {
      document.body.classList.remove('reading-page');
      
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
      }
      
      isAutoPlayingRef.current = false;
      isPlayingRef.current = false;
      playbackIdRef.current++;
      
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      
      // 清理录音相关资源
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      
      if (recorderRef.current && isRecording) {
        try {
          recorderRef.current.stop();
        } catch (e) {
          console.warn('停止录音失败:', e);
        }
      }
      
      // 组件卸载时清除 sessionStorage 中的领读数据和访问标记
      sessionStorage.removeItem('readingData');
      sessionStorage.removeItem('readingPageVisited');
    };
  }, []);

  // 同步状态到 ref，并重置图片加载错误状态，以及滚动到当前单词
  useEffect(() => {
    currentWordIndexRef.current = currentWordIndex;
    // 切换单词时重置图片加载错误状态
    setImageLoadError(false);
    
    // 自动滚动到当前聚焦的单词
    if (wordListItemsRef.current[currentWordIndex]) {
      wordListItemsRef.current[currentWordIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [currentWordIndex]);

  useEffect(() => {
    readingSettingsRef.current = readingSettings;
  }, [readingSettings]);

  useEffect(() => {
    selectedWordsRef.current = selectedWords;
  }, [selectedWords]);

  // 从 sessionStorage 获取选中的单词数据和设置（避免URL过长导致431错误）
  useEffect(() => {
    const initializeReading = async () => {
      try {
        // 优先从 sessionStorage 获取数据
        const sessionData = sessionStorage.getItem('readingData');
        let parsedData = null;
        
        // 检查是否是页面刷新
        const isPageRefresh = sessionStorage.getItem('readingPageVisited') === 'true';
        
        if (sessionData) {
          parsedData = JSON.parse(sessionData);
          // 不要立即删除，支持页面刷新
        } else {
          // 兼容旧的 URL 参数方式
          const urlParams = new URLSearchParams(window.location.search);
          const dataParam = urlParams.get('data');
          if (dataParam) {
            parsedData = JSON.parse(decodeURIComponent(dataParam));
          }
        }
        
        if (parsedData) {
          const wordsData = parsedData.words || [];
          const settings = parsedData.settings;
          
          if (wordsData.length > 0) {
            setSelectedWords(wordsData);
            
            // 使用要应用的设置（如果有的话）
            const settingsToUse = settings || readingSettings;
            if (settings) {
              setReadingSettings(settings);
              readingSettingsRef.current = settings; // 立即更新 ref
            }
            
            // 标记页面已访问（用于检测刷新）
            if (!isPageRefresh) {
              sessionStorage.setItem('readingPageVisited', 'true');
            }
            
            // 默认进入暂停状态，不自动播放
            setIsAutoPlaying(false);
            isAutoPlayingRef.current = false;
          } else {
            setError('未找到领读数据');
          }
        } else {
          setError('未找到领读数据');
        }
      } catch (err) {
        console.error('解析领读数据失败:', err);
        setError('领读数据格式错误');
      } finally {
        setLoading(false);
      }
    };
    
    initializeReading();
  }, []);

  // 播放单词音频（包含跟读次数）
  const playWordAudio = async (word, customSettings = null) => {
    // 增加播放ID，这会使之前的播放自动失效
    const currentPlaybackId = ++playbackIdRef.current;
    
    const currentSettings = customSettings || readingSettings;
    
    // 停止当前正在播放的音频
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    
    if (!word.audio || !word.audio.res) {
      return;
    }
    
    // 再次检查playbackId（防止在停止音频期间又有新的播放请求）
    if (currentPlaybackId !== playbackIdRef.current) {
      return;
    }
    
    // 始终设置为播放状态
    isPlayingRef.current = true;
    setIsPlaying(true);
    
    try {
      const audioUrl = word.audio.res;
      const playCount = currentSettings.playCount;
      const repeatCount = currentSettings.repeatCount;
      const pauseTimeMultiplier = currentSettings.pauseTime;
      
      const testResponse = await fetch(audioUrl, { method: 'HEAD' });
      if (!testResponse.ok) {
        throw new Error(`音频文件不可访问: ${testResponse.status}`);
      }
      
      const audio = new Audio(audioUrl);
      setCurrentAudio(audio);
      
      const playbackRate = currentSettings.playSpeed === 'fast' ? 1.5 : 
                          currentSettings.playSpeed === 'slow' ? 0.75 : 1;
      audio.playbackRate = playbackRate;
      
      await new Promise((resolve, reject) => {
        audio.onloadedmetadata = () => {
          audio.playbackRate = playbackRate;
          resolve();
        };
        audio.onerror = reject;
        audio.load();
      });
      
      const audioDuration = audio.duration;
      // 留白时间 = 向上取整(音频时长) × 倍数
      const pauseTime = Math.ceil(audioDuration) * pauseTimeMultiplier * 1000;
      
      // 根据跟读次数模式选择不同的播放逻辑
      if (repeatCount === 'playOneReadOne') {
        // 播一次读一次：单词-留白-单词-留白-单词-留白
        for (let i = 0; i < playCount; i++) {
          // 检查播放是否被取消
          if (currentPlaybackId !== playbackIdRef.current) {
            break;
          }
          
          // 播放单词
          await new Promise((resolve, reject) => {
            audio.currentTime = 0;
            
            const onEnded = () => {
              audio.removeEventListener('ended', onEnded);
              audio.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = (error) => {
              audio.removeEventListener('ended', onEnded);
              audio.removeEventListener('error', onError);
              reject(error);
            };
            
            audio.addEventListener('ended', onEnded);
            audio.addEventListener('error', onError);
            
            audio.playbackRate = playbackRate;
            audio.play().catch(reject);
          });
          
          // 每次播放后都加留白
          if (pauseTime > 0 && currentPlaybackId === playbackIdRef.current) {
            // 在每次留白期间都显示倒计时
            const countdownSeconds = Math.ceil(pauseTime / 1000);
            startCountdown(countdownSeconds);
            
            await new Promise(resolve => {
              const timeout = setTimeout(resolve, pauseTime);
              const checkCancel = () => {
                if (currentPlaybackId !== playbackIdRef.current) {
                  clearTimeout(timeout);
                  resolve();
                } else {
                  setTimeout(checkCancel, 100);
                }
              };
              checkCancel();
            });
          }
        }
      } else {
        // 播完读一次：单词-单词-单词-留白
        for (let i = 0; i < playCount; i++) {
          // 检查播放是否被取消
          if (currentPlaybackId !== playbackIdRef.current) {
            break;
          }
          
          // 播放单词
          await new Promise((resolve, reject) => {
            audio.currentTime = 0;
            
            const onEnded = () => {
              audio.removeEventListener('ended', onEnded);
              audio.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = (error) => {
              audio.removeEventListener('ended', onEnded);
              audio.removeEventListener('error', onError);
              reject(error);
            };
            
            audio.addEventListener('ended', onEnded);
            audio.addEventListener('error', onError);
            
            audio.playbackRate = playbackRate;
            audio.play().catch(reject);
          });
        }
        
        // 所有播放完成后，最后加一次留白
        if (pauseTime > 0 && currentPlaybackId === playbackIdRef.current) {
          // 在留白期间显示倒计时
          const countdownSeconds = Math.ceil(pauseTime / 1000);
          startCountdown(countdownSeconds);
          
          await new Promise(resolve => {
            const timeout = setTimeout(resolve, pauseTime);
            const checkCancel = () => {
              if (currentPlaybackId !== playbackIdRef.current) {
                clearTimeout(timeout);
                resolve();
              } else {
                setTimeout(checkCancel, 100);
              }
            };
            checkCancel();
          });
        }
      }
      
    } catch (error) {
      console.error('音频播放失败:', error);
    } finally {
      // 只有当前播放ID还有效时，才清理状态
      if (currentPlaybackId === playbackIdRef.current) {
        isPlayingRef.current = false;
        setIsPlaying(false);
        setCurrentAudio(null);
        setCountdown(0); // 清除倒计时/播放状态显示
        
        // 如果是自动播放模式，继续播放下一个
        if (isAutoPlayingRef.current && currentWordIndexRef.current < selectedWordsRef.current.length - 1) {
          setTimeout(() => {
            if (isAutoPlayingRef.current) {
              autoPlayNext();
            }
          }, 500);
        } else if (isAutoPlayingRef.current && currentWordIndexRef.current === selectedWordsRef.current.length - 1) {
          setIsAutoPlaying(false);
          isAutoPlayingRef.current = false;
        }
      }
    }
  };

  // 停止当前音频播放
  const stopCurrentAudio = () => {
    // 增加播放ID来取消当前播放
    playbackIdRef.current++;
    
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    setCurrentAudio(null);
    isPlayingRef.current = false;
    setIsPlaying(false);
  };

  // 清除倒计时
  const clearCountdown = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
  };

  // 开始倒计时（只用于显示，不额外增加等待时间）
  const startCountdown = (seconds) => {
    // 清除之前的倒计时
    clearCountdown();
    
    // 设置初始倒计时
    setCountdown(seconds);
    
    const runCountdown = (remaining) => {
      if (remaining > 0) {
        countdownTimerRef.current = setTimeout(() => {
          setCountdown(remaining - 1);
          runCountdown(remaining - 1);
        }, 1000);
      } else {
        // 倒计时结束
        setCountdown(0);
      }
    };
    
    runCountdown(seconds);
  };

  // 获取当前单词
  const getCurrentWord = () => {
    return selectedWords[currentWordIndex] || null;
  };

  // 自动播放下一个单词
  const autoPlayNext = () => {
    const currentIndex = currentWordIndexRef.current;
    const words = selectedWordsRef.current;
    
    if (currentIndex < words.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentWordIndex(newIndex);
      currentWordIndexRef.current = newIndex;
      
      isPlayingRef.current = true;
      setIsPlaying(true);
      
      setTimeout(() => {
        if (words[newIndex] && isAutoPlayingRef.current) {
          playWordAudio(words[newIndex], readingSettingsRef.current);
        } else {
          isPlayingRef.current = false;
          setIsPlaying(false);
        }
      }, 150);
    } else {
      setIsAutoPlaying(false);
      isAutoPlayingRef.current = false;
    }
  };

  // 下一个单词
  const nextWord = () => {
    setIsAutoPlaying(false);
    isAutoPlayingRef.current = false;
    
    // 清除倒计时
    clearCountdown();
    
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    if (isPlaying) {
      stopCurrentAudio();
    }
    
    if (currentWordIndex < selectedWords.length - 1) {
      const newIndex = currentWordIndex + 1;
      setCurrentWordIndex(newIndex);
      currentWordIndexRef.current = newIndex;
      
      // 切换后不自动播放
      isPlayingRef.current = false;
      setIsPlaying(false);
    }
  };

  // 上一个单词
  const prevWord = () => {
    setIsAutoPlaying(false);
    isAutoPlayingRef.current = false;
    
    // 清除倒计时
    clearCountdown();
    
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    if (isPlaying) {
      stopCurrentAudio();
    }
    
    if (currentWordIndex > 0) {
      const newIndex = currentWordIndex - 1;
      setCurrentWordIndex(newIndex);
      currentWordIndexRef.current = newIndex;
      
      // 切换后不自动播放
      isPlayingRef.current = false;
      setIsPlaying(false);
    }
  };

  // 切换自动播放
  const toggleAutoPlay = () => {
    const newAutoPlayState = !isAutoPlaying;
    setIsAutoPlaying(newAutoPlayState);
    isAutoPlayingRef.current = newAutoPlayState;
    
    // 如果关闭自动播放，清除倒计时
    if (!newAutoPlayState) {
      clearCountdown();
    }
    
    if (newAutoPlayState && !isPlaying) {
      const currentWord = selectedWords[currentWordIndex];
      if (currentWord) {
        playWordAudio(currentWord, readingSettings);
      }
    }
  };
  
  // 切换图片显示
  const handleToggleImage = () => {
    setShowImage(prev => !prev);
  };
  
  const handleTogglePhonetic = () => {
    setShowPhonetic(prev => !prev);
  };
  
  const handleToggleTranslation = () => {
    setShowTranslation(prev => !prev);
  };
  
  // 听按钮点击处理
  const handleListenClick = () => {
    const currentWord = selectedWords[currentWordIndex];
    if (currentWord && currentWord.audio && currentWord.audio.res) {
      // 停止自动播放
      setIsAutoPlaying(false);
      isAutoPlayingRef.current = false;
      
      // 直接播放当前单词（playWordAudio内部会处理停止当前音频）
      playWordAudio(currentWord, readingSettings);
    }
  };
  
  // 打开录音权限并开始录音
  const startRecording = () => {
    try {
      console.log('startRecording called, isAutoPlaying:', isAutoPlaying, 'isPlaying:', isPlaying);
      
      // 如果正在自动播放，先暂停自动播放
      if (isAutoPlaying) {
        console.log('暂停自动播放');
        setIsAutoPlaying(false);
        isAutoPlayingRef.current = false;
        clearCountdown();
      }
      
      // 无论是否自动播放，只要有音频在播放就停止
      if (isPlaying) {
        console.log('停止当前音频播放');
        stopCurrentAudio();
      }
      
      // 检查浏览器支持
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('您的浏览器不支持录音功能，请使用Chrome、Firefox或Safari等现代浏览器');
        return;
      }
      
      if (!recorderRef.current) {
        initRecorder();
      }
      
      const recorder = recorderRef.current;
      
      if (!recorder) {
        alert('录音器初始化失败，请刷新页面重试');
        return;
      }
      
      recorder.open(function () {
        console.log("录音权限已获取，准备开始录音");
        
        try {
          // 重置状态
          setRecordingDuration(0);
          setRecordingPowerLevel(0);
          setEvaluationResult(null);
          setIsRecording(true);
          
          // 延迟开始录音，避免录到点击按钮的声音
          setTimeout(() => {
            if (recorderRef.current) {
              recorder.start();
              console.log("开始录音...");
            }
          }, 300);
          
          // 开始计时（注意：实际录音会延迟300ms开始）
          recordingTimerRef.current = setInterval(() => {
            setRecordingDuration(prev => prev + 1);
          }, 1000);
          
        } catch (error) {
          console.error('开始录音失败:', error);
          alert('开始录音失败，请重试');
          setIsRecording(false);
        }
        
      }, function (msg, isUserNotAllow) {
        console.error("录音权限获取失败：" + msg);
        
        let errorMessage = "录音权限获取失败：" + msg;
        if (isUserNotAllow) {
          errorMessage = "用户拒绝了录音权限。请在浏览器设置中允许录音权限，然后刷新页面重试。";
        } else if (msg.includes('NotFoundError')) {
          errorMessage = "未找到录音设备，请检查麦克风是否正确连接。";
        } else if (msg.includes('NotAllowedError')) {
          errorMessage = "录音权限被拒绝，请在浏览器设置中允许录音权限。";
        }
        
        alert(errorMessage);
      });
      
    } catch (error) {
      console.error('启动录音失败:', error);
      alert('启动录音失败：' + error.message);
    }
  };
  
  // 停止录音并获取音频数据
  const stopRecording = async () => {
    if (!recorderRef.current || !isRecording) {
      return;
    }
    
    try {
      const recorder = recorderRef.current;
      
      recorder.stop(async function (blob, duration) {
        console.log("录音完成，时长：" + Math.floor(duration / 1000) + "秒");
        console.log("音频格式信息:", {
          type: blob.type,
          size: blob.size,
          duration: duration
        });
        setIsRecording(false);
        
        // 清除计时器
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
        // 检查录音时长
        const durationSeconds = Math.floor(duration / 1000);
        if (durationSeconds < 1) {
          alert('录音时间太短，请至少录制1秒钟');
          return;
        }
        
        if (durationSeconds > 60) {
          alert('录音时间过长，请录制60秒以内的音频');
          return;
        }
        
        // 检查音频数据
        if (!blob || blob.size === 0) {
          alert('录音数据为空，请重新录音');
          return;
        }
        
        // 直接使用 TRecorder 生成的 MP3 blob，不进行格式转换
        // 注意：TRecorder 已经配置为 MP3 格式和 44100Hz 采样率
        // 不进行裁剪，以保持原始的 MP3 格式和采样率
        
        // 获取当前单词进行评测
        const currentWord = selectedWords[currentWordIndex];
        if (currentWord && currentWord.title) {
          evaluateVoice(blob, currentWord.title);
        } else {
          alert('当前单词数据异常，无法进行评测');
        }
        
      }, function (msg) {
        console.error("录音停止失败：" + msg);
        setIsRecording(false);
        
        // 清除计时器
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
        alert('录音停止失败：' + msg);
      });
      
      recorderRef.current = null;
      
    } catch (error) {
      console.error('停止录音异常:', error);
      setIsRecording(false);
      
      // 清除计时器
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      alert('停止录音失败：' + error.message);
    }
  };
  
  // 语音评测 API 调用（创建音频 URL 并先播放）
  const evaluateVoice = async (audioBlob, content) => {
    try {
      // 验证输入参数
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('录音数据为空，请重新录音');
      }
      
      if (!content || content.trim() === '') {
        throw new Error('评测文本为空');
      }
      
      // 检查音频大小（限制为10MB）
      if (audioBlob.size > 10 * 1024 * 1024) {
        throw new Error('录音文件过大，请录制较短的音频');
      }
      
      // 创建音频 URL 用于播放
      const audioUrl = URL.createObjectURL(audioBlob);
      setRecordedAudioUrl(audioUrl);
      recordedAudioUrlRef.current = audioUrl; // 同时保存到 ref
      
      // 先播放用户录音，播放完成后再进行评测
      playRecordedAudio(audioUrl, audioBlob, content);
      
    } catch (error) {
      alert(error.message);
      
      // 清理录音 URL
      if (recordedAudioUrlRef.current) {
        URL.revokeObjectURL(recordedAudioUrlRef.current);
        setRecordedAudioUrl(null);
        recordedAudioUrlRef.current = null;
      }
    }
  };
  
  // 实际的评测 API 调用
  const performEvaluation = async (audioBlob, content) => {
    setIsEvaluating(true);
    setEvaluationResult(null);
    
    try {
      // 验证输入参数
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('录音数据为空，请重新录音');
      }
      
      if (!content || content.trim() === '') {
        throw new Error('评测文本为空');
      }
      
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.mp3');
      formData.append('content', content);
      formData.append('eval_type', 1); // 英文单词评测
      formData.append('user_id', 'user12345'); // 可以从用户状态获取
      formData.append('audio_type', 1); // MP3格式
      
      // 设置请求超时
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30秒超时
      
      const result = await voiceAPI.evaluate(formData, controller.signal);
      clearTimeout(timeoutId);
      
      if (result.code === 200) {
        if (result.data && typeof result.data.score === 'number') {
          setEvaluationResult(result.data);
          console.log('评测结果:', result.data);
          
          // 根据分数显示对应的图片
          const score = result.data.score;
          let imageType = '';
          if (score < 60) {
            imageType = '测评差';
          } else if (score >= 60 && score < 80) {
            imageType = '测评中';
          } else if (score >= 80) {
            imageType = '测评好';
          }
          
          setScoreImageType(imageType);
          setShowScoreImage(true);
          
          // 2秒后隐藏图片
          setTimeout(() => {
            setShowScoreImage(false);
          }, 1500);
        } else {
          throw new Error('评测结果格式异常');
        }
      } else {
        throw new Error(result.message || '评测失败');
      }
      
    } catch (error) {
      console.error('语音评测失败:', error);
      
      let errorMessage = '语音评测失败';
      if (error.name === 'AbortError') {
        errorMessage = '评测请求超时，请检查网络连接';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = '网络连接失败，请检查评测服务是否启动';
      } else {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
      setEvaluationResult({
        error: true,
        message: errorMessage
      });
    } finally {
      setIsEvaluating(false);
      
      // 清理录音 URL
      if (recordedAudioUrlRef.current) {
        URL.revokeObjectURL(recordedAudioUrlRef.current);
        setRecordedAudioUrl(null);
        recordedAudioUrlRef.current = null;
      }
    }
  };

  // 播放录音
  const playRecordedAudio = async (audioUrl, audioBlob = null, content = null) => {
    try {
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      setCurrentAudio(audio);
      setIsPlaying(true);
      
      // 设置播放结束回调
      audio.onended = () => {
        currentAudioRef.current = null;
        setCurrentAudio(null);
        setIsPlaying(false);
        
        // 播放完成后，如果有 audioBlob 和 content，则进行评测
        if (audioBlob && content) {
          performEvaluation(audioBlob, content);
        } else {
          // 如果没有评测数据，直接清理
          if (recordedAudioUrlRef.current) {
            URL.revokeObjectURL(recordedAudioUrlRef.current);
            setRecordedAudioUrl(null);
            recordedAudioUrlRef.current = null;
          }
        }
      };
      
      audio.onerror = (e) => {
        currentAudioRef.current = null;
        setCurrentAudio(null);
        setIsPlaying(false);
        
        console.error('录音播放失败:', e);
        alert('播放录音失败，请重试');
        
        // 清理录音 URL
        if (recordedAudioUrlRef.current) {
          URL.revokeObjectURL(recordedAudioUrlRef.current);
          setRecordedAudioUrl(null);
          recordedAudioUrlRef.current = null;
        }
      };
      
      await audio.play();
    } catch (error) {
      console.error('播放录音失败:', error);
      alert('播放录音失败：' + error.message);
      
      currentAudioRef.current = null;
      setCurrentAudio(null);
      setIsPlaying(false);
      
      // 清理录音 URL
      if (recordedAudioUrlRef.current) {
        URL.revokeObjectURL(recordedAudioUrlRef.current);
        setRecordedAudioUrl(null);
        recordedAudioUrlRef.current = null;
      }
    }
  };
  
  // 说按钮点击处理
  const handleSpeakClick = () => {
    console.log('handleSpeakClick called, isRecording:', isRecording);
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  
  const handleSpeedChange = (speed) => {
    setReadingSettings(prev => ({
      ...prev,
      playSpeed: speed
    }));
  };
  
  // 返回单词选择页面
  const handleBack = () => {
    window.history.back();
  };
  
  // 打开领读设置弹窗
  const handleOpenReadingSettings = () => {
    setShowReadingSettings(true);
  };
  
  // 关闭领读设置弹窗
  const handleReadingSettingsClose = () => {
    setShowReadingSettings(false);
  };
  
  // 更新领读设置
  const handleReadingSettingsUpdate = (newSettings) => {
    setReadingSettings(newSettings);
    readingSettingsRef.current = newSettings;
    setShowReadingSettings(false);
  };

  // 渲染加载状态
  if (loading) {
    return (
      <div className="reading-container">
        <div className="reading-loading">
          <div className="loading-spinner"></div>
          <p>正在准备领读...</p>
        </div>
      </div>
    );
  }

  // 渲染错误状态
  if (error) {
    return (
      <div className="reading-container">
        <div className="reading-error">
          <p>{error}</p>
          <button className="back-button" onClick={handleBack}>
            返回单词选择
          </button>
        </div>
      </div>
    );
  }

  const currentWord = getCurrentWord();

  return (
    <div className="reading-container">
      {/* Logo区域 */}
      <div className="reading-header">
        <img 
          src={logoImage} 
          alt="大鱼人机口语" 
          className="reading-logo"
        />
      </div>
      
      {/* 左侧区域 */}
      <div className="reading-left">
        {/* 第一部分 - 单词显示区 */}
        <div className={`reading-section-1 ${isAutoPlaying ? 'playing' : ''}`}>
          {/* 顶部蓝色半圆文字 */}
          <div className="top-semicircle-text">
            {isAutoPlaying ? (countdown > 0 ? `${countdown}s` : '播放') : '暂停'}
          </div>
          <div className={`reading-content ${showImage ? 'with-image' : 'without-image'}`}>
            {/* 进度显示 */}
            <div className="reading-progress">
              {currentWordIndex + 1}/{selectedWords.length}
            </div>

            {/* 图片显示区域 */}
            {showImage && currentWord && currentWord.images && currentWord.images.length > 0 && !imageLoadError && (
              <div className="reading-image-container">
                <img 
                  src={currentWord.images[0].res} 
                  alt={currentWord.title}
                  className="reading-word-image"
                  onError={() => {
                    setImageLoadError(true);
                  }}
                />
              </div>
            )}

            {/* 单词显示区域 */}
            <div className="reading-word-display">
              <div 
                className="reading-word-english"
                style={currentWord ? getWordStyle(currentWord.title) : {}}
                dangerouslySetInnerHTML={{ __html: currentWord ? formatWordTitle(currentWord.title) : '' }}
              />
              {showPhonetic && (
                <div className="reading-word-phonetic">
                  {currentWord && currentWord.phonetic ? currentWord.phonetic : ''}
                </div>
              )}
              {showTranslation && (
                <div className="reading-word-translation">
                  {currentWord && currentWord.en_title ? currentWord.en_title : ''}
                </div>
              )}
              
              {/* 听和说按钮 */}
              <div className="listen-speak-buttons">
                <button 
                  className="action-btn listen-btn"
                  onClick={handleListenClick}
                  title="听"
                >
                  <img src={listenIcon} alt="听" className="action-icon" />
                </button>
                
                <button 
                  className={`action-btn speak-btn ${isRecording ? 'recording' : ''} ${isEvaluating ? 'evaluating' : ''}`}
                  onClick={handleSpeakClick}
                  title={isRecording ? "停止录音" : "开始录音"}
                  disabled={isEvaluating}
                >
                  <img src={speakIcon} alt="说" className="action-icon" />
                  {isRecording && (
                    <div className="status-text">正在录音...</div>
                  )}
                  {isEvaluating && (
                    <div className="status-text">正在测评...</div>
                  )}
                </button>
              </div>
              

            </div>
          </div>
        </div>
        
        {/* 第二部分 - 控制按钮区 */}
        <div className="reading-section-2">
          <div className="reading-controls">
            {/* 左侧控制按钮区 */}
            <div className="controls-left">
              <button 
                className="control-btn prev-btn"
                onClick={prevWord}
                disabled={currentWordIndex === 0}
                title="上一个单词"
              >
                上一个
              </button>
              
              <button 
                className={`control-btn auto-play-btn ${isAutoPlaying ? 'active' : ''}`}
                onClick={toggleAutoPlay}
                title={isAutoPlaying ? '停止自动播放' : '开始自动播放'}
              >
                <div className="auto-play-icon">
                  {isAutoPlaying ? (
                    <>
                      <div className="pause-bar"></div>
                      <div className="pause-bar"></div>
                    </>
                  ) : (
                    <div className="play-triangle"></div>
                  )}
                </div>
              </button>
              
              <button 
                className="control-btn next-btn"
                onClick={nextWord}
                disabled={currentWordIndex === selectedWords.length - 1}
                title="下一个单词"
              >
                下一个
              </button>
              
              <button 
                className="control-btn settings-btn"
                onClick={handleOpenReadingSettings}
                title="领读设置"
              >
                领读设置
              </button>
            </div>
            
            {/* 中间开关按钮区 */}
            <div className="toggle-switch-group">
              <label className="toggle-switch-label">
                <div 
                  className={`toggle-switch ${showImage ? 'active' : ''}`}
                  onClick={handleToggleImage}
                  title="显示/隐藏图片"
                >
                  <div className="toggle-slider"></div>
                </div>
                <span className="toggle-label-text">图片</span>
              </label>
              
              <label className="toggle-switch-label">
                <div 
                  className={`toggle-switch ${showPhonetic ? 'active' : ''}`}
                  onClick={handleTogglePhonetic}
                  title="显示/隐藏音标"
                >
                  <div className="toggle-slider"></div>
                </div>
                <span className="toggle-label-text">音标</span>
              </label>
              
              <label className="toggle-switch-label">
                <div 
                  className={`toggle-switch ${showTranslation ? 'active' : ''}`}
                  onClick={handleToggleTranslation}
                  title="显示/隐藏翻译"
                >
                  <div className="toggle-slider"></div>
                </div>
                <span className="toggle-label-text">翻译</span>
              </label>
              
              <div className="speed-selector-wrapper">
                <select 
                  className="speed-selector"
                  value={readingSettings.playSpeed}
                  onChange={(e) => handleSpeedChange(e.target.value)}
                  title="选择播放速度"
                >
                  <option value="slow">慢速</option>
                  <option value="normal">正常</option>
                  <option value="fast">快速</option>
                </select>
                <label className="speed-selector-label">播放速度</label>
              </div>
            </div>
            
            {/* 右侧功能按钮区 */}
            <div className="controls-right">
              <button 
                className="control-btn back-btn"
                onClick={handleBack}
                title="返回单词选择页面"
              >
                返回同步词汇
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* 右侧区域 - 单词列表 */}
      <div className="reading-right">
        <div className="reading-words-list">
          <div className="words-list-header">
            <h3>已选词汇 ({selectedWords.length})</h3>
          </div>
          <div className="words-list-content">
            {selectedWords.map((word, index) => (
              <div 
                key={word.id} 
                ref={(el) => (wordListItemsRef.current[index] = el)}
                className={`word-list-item ${index === currentWordIndex ? 'active' : ''}`}
                onClick={() => {
                  setIsAutoPlaying(false);
                  isAutoPlayingRef.current = false;
                  
                  // 清除倒计时
                  clearCountdown();
                  
                  if (navigationTimeoutRef.current) {
                    clearTimeout(navigationTimeoutRef.current);
                  }
                  
                  if (isPlaying) {
                    stopCurrentAudio();
                  }
                  
                  setCurrentWordIndex(index);
                  currentWordIndexRef.current = index;
                  
                  // 切换后不自动播放
                  isPlayingRef.current = false;
                  setIsPlaying(false);
                }}
              >
                <div className="word-list-item-english">{word.title}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* 领读设置弹窗 */}
      <SettingsModal
        isOpen={showReadingSettings}
        onClose={handleReadingSettingsClose}
        onStart={handleReadingSettingsUpdate}
        title={readingConfig.title}
        settingItems={readingConfig.settingItems}
        defaultSettings={readingSettings}
      />
      
      {/* 评测结果图片弹窗 */}
      {showScoreImage && (
        <div className="score-image-overlay" onClick={() => setShowScoreImage(false)}>
          <div className="score-image-container">
            <img 
              src={
                scoreImageType === '测评好' ? scoreGoodImage :
                scoreImageType === '测评差' ? scoreBadImage :
                scoreImageType === '测评中' ? scoringImage : ''
              } 
              alt={scoreImageType}
              className="score-image"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Reading;

